/* eslint-disable no-console */
/**
 * Instrumentation Module
 *
 * This module provides a unified API for multiple monitoring/instrumentation providers
 * (Sentry, DataDog, etc.) and should be imported FIRST before any other imports.
 *
 * @example
 * // In main.tsx (must be first import)
 * import { instrumentation } from './instrument';
 *
 * // Use the unified API
 * instrumentation.captureException(error);
 * instrumentation.setUser({ id: '123', email: 'user@example.com' });
 */

import { datadogProvider, sentryProvider } from './providers';
import type {
  ErrorContext,
  Instrumentation,
  InstrumentationProvider,
  LogLevel,
  ProviderConfig,
  ReactErrorHandler,
  UserInfo,
} from './types';

// Re-export types for consumers
export type {
  ErrorContext,
  Instrumentation,
  InstrumentationProvider,
  LogLevel,
  ProviderConfig,
  ReactErrorHandler,
  UserInfo,
} from './types';

// Re-export createBrowserRouter from DataDog for router integration
export { createBrowserRouter } from '@datadog/browser-rum-react/react-router-v7';

/**
 * Get environment from Vite env vars
 * We access this directly since this file loads before config
 */
const getEnvironment = (): string => {
  return (import.meta.env.VITE_APP_NODE_ENV as string) ?? 'development';
};

/**
 * Check if we're in production environment
 */
const isProduction = (): boolean => {
  return getEnvironment() === 'production';
};

/**
 * Creates the instrumentation orchestrator that manages all providers
 */
function createInstrumentation(
  providers: InstrumentationProvider[]
): Instrumentation {
  let initialized = false;

  return {
    init(): void {
      if (initialized) {
        return;
      }

      const config: ProviderConfig = {
        enabled: isProduction(),
        environment: getEnvironment(),
      };

      for (const provider of providers) {
        try {
          provider.init(config);
        } catch (error) {
          console.error(
            `[Instrumentation] Failed to initialize ${provider.name}:`,
            error
          );
        }
      }

      initialized = true;
    },

    captureException(error: Error, context?: ErrorContext): void {
      for (const provider of providers) {
        try {
          provider.captureException(error, context);
        } catch (error_) {
          console.error(
            `[Instrumentation] ${provider.name} captureException failed:`,
            error_
          );
        }
      }
    },

    captureMessage(message: string, level: LogLevel = 'info'): void {
      for (const provider of providers) {
        try {
          provider.captureMessage(message, level);
        } catch (error) {
          console.error(
            `[Instrumentation] ${provider.name} captureMessage failed:`,
            error
          );
        }
      }
    },

    setUser(user: UserInfo | null): void {
      for (const provider of providers) {
        try {
          provider.setUser(user);
        } catch (error) {
          console.error(
            `[Instrumentation] ${provider.name} setUser failed:`,
            error
          );
        }
      }
    },

    setContext(key: string, value: Record<string, unknown>): void {
      for (const provider of providers) {
        try {
          provider.setContext(key, value);
        } catch (error) {
          console.error(
            `[Instrumentation] ${provider.name} setContext failed:`,
            error
          );
        }
      }
    },

    addBreadcrumb(breadcrumb: {
      category: string;
      message: string;
      level?: LogLevel;
      data?: Record<string, unknown>;
    }): void {
      for (const provider of providers) {
        try {
          provider.addBreadcrumb(breadcrumb);
        } catch (error) {
          console.error(
            `[Instrumentation] ${provider.name} addBreadcrumb failed:`,
            error
          );
        }
      }
    },

    getReactErrorHandler(): ReactErrorHandler {
      const handlers = providers.map(p => p.getReactErrorHandler());

      return (error, errorInfo) => {
        for (const handler of handlers) {
          try {
            handler(error, errorInfo);
          } catch (error_) {
            console.error(
              '[Instrumentation] React error handler failed:',
              error_
            );
          }
        }
      };
    },

    getProviders(): InstrumentationProvider[] {
      return [...providers];
    },
  };
}

/**
 * The main instrumentation instance with all registered providers
 *
 * Providers are registered in order of priority:
 * 1. Sentry - Primary error tracking
 * 2. DataDog - RUM and performance monitoring
 */
export const instrumentation = createInstrumentation([
  sentryProvider,
  datadogProvider,
]);

// Auto-initialize on import (this file should be imported first in main.tsx)
instrumentation.init();
